import { type FC, type RefObject, useState } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { Carousel } from 'dibs-carousel';
import { SharedCarouselDot } from '../../shared/SharedCarouselDot/SharedCarouselDot.tsx';
import { SbSharedL1ModuleTile } from '../SbSharedL1ModuleTile/SbSharedL1ModuleTile.tsx';

// types
import { type SbSharedL1ModuleBanner_l1Module$key } from './__generated__/SbSharedL1ModuleBanner_l1Module.graphql.ts';

//styles
import styles from './SbSharedL1ModuleBanner.module.css';

type Props = {
    l1Module: SbSharedL1ModuleBanner_l1Module$key | null | undefined;
    l1ModuleContainerRef: RefObject<HTMLDivElement | null>;
};

const l1ModuleFragment = graphql`
    fragment SbSharedL1ModuleBanner_l1Module on L1Module {
        banners {
            ...SbSharedL1ModuleTile_l1ModuleItemTile
        }
    }
`;

export const SbSharedL1ModuleBanner: FC<Props> = ({
    l1Module: l1ModuleRef,
    l1ModuleContainerRef,
}) => {
    const l1Module = useFragment(l1ModuleFragment, l1ModuleRef);
    const [isCarouselVisible, setIsCarouselVisible] = useState<boolean>(false);
    const { banners } = l1Module || {};

    const bannersLength = banners?.length || 0;

    if (!bannersLength) {
        return null;
    }

    return (
        <>
            <VisibilityTracker
                watchAfterFirstVisible
                elementRef={l1ModuleContainerRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible !== isCarouselVisible) {
                        setIsCarouselVisible(isVisible);
                    }
                }}
            />
            <Carousel
                isAutoRun={isCarouselVisible && bannersLength > 1}
                classNames={{ wrapper: styles.carousel }}
                dataTn="l1-banner-carousel"
                showDots={bannersLength > 1}
                hideArrows
                totalItems={bannersLength}
                itemsPerPage={1}
                step={1}
                renderItem={({ index }: { index: number }) =>
                    banners?.[index] ? (
                        <SbSharedL1ModuleTile
                            l1ModuleItemTile={banners[index]}
                            creative="Middle Tile"
                            position={index + 1}
                            isBannerTile
                        />
                    ) : null
                }
                renderDot={({ isCurrentDot }: { isCurrentDot?: boolean } = {}) => (
                    <SharedCarouselDot isActive={!!isCurrentDot} />
                )}
            />
        </>
    );
};
